import { useEffect, useRef, useState } from "preact/hooks"
import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Trash2,
  FileText,
  Calculator,
  RotateCcw,
  AlertTriangle,
  ExternalLink,
  ListTree,
  Plus,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { FileViewer } from "@/accounting/file_viewer.tsx"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  AiParseResponse,
  AiParsedBackupLine,
  AiParsedDeduction,
  AiParsedResult,
  CreatedDeductionSummary,
  SkippedDeductionSummary,
  ConfirmAiImportResponse,
  aiImportConfirm,
} from "@/api/ai_import"

interface AiImportReviewProps {
  parseResponse: AiParseResponse
  onReset: () => void
  /** A check typed in by hand: no document, and the check number, date and amount are editable. */
  manual?: boolean
  className?: string
}

type Step = "review" | "confirming" | "success"

/** Map API / legacy strings to canonical UI values for radios and confirm. */
function normalizeAiImportMethodForUi(raw: string): string {
  const t = raw.trim()
  const lower = t.toLowerCase()
  if (
    t === "Pay Customer Directly" ||
    lower === "paid directly" ||
    lower === "pay customer directly" ||
    lower === "check request"
  ) {
    return "Pay Customer Directly"
  }
  if (t === "DEDUCT" || lower === "deducted" || lower === "deduct" || lower === "deducted off check") {
    return "DEDUCT"
  }
  return t
}

function initializePending(parsed: AiParsedResult, deductionMethod: string): AiParsedResult {
  return {
    ...parsed,
    deductions: parsed.deductions.map((d: AiParsedDeduction) => ({
      ...d,
      pending: d.pending ?? (deductionMethod === "Pay Customer Directly"),
    })),
  }
}

/**
 * A new empty row. A check has one payer, so it takes the row above's; `key` only has to be
 * unique within the table (it is the row's React key and never reaches the server's dedup).
 */
export function blank_line(previous: AiParsedDeduction | null, key: string, kind: string, pending: boolean): AiParsedDeduction {
  return {
    remote_id: key,
    invoice_number: "",
    invoice_amount: 0,
    source: previous?.source ?? "Other",
    original_source: previous?.original_source ?? null,
    kind,
    pending,
  }
}

/** An empty review table for a check entered by hand, opened with one invoice line. */
export function manual_check_response(deduction_method: string): AiParseResponse {
  return {
    s3_uri: "",
    deduction_method,
    parsed: {
      file_type: "check",
      check_number: null,
      check_date: null,
      check_amount: null,
      deductions: [blank_line(null, "manual-0", "invoice", deduction_method === "Pay Customer Directly")],
    },
  }
}

/**
 * What the rows add up to, in cents, signed the way the server books them: a deduction is always
 * negative and an invoice or repayment always positive, whatever sign was typed.
 */
export function lines_total_cents(deductions: AiParsedDeduction[]): number {
  return deductions.reduce((total: number, d: AiParsedDeduction) => {
    const cents = Math.abs(Math.round((d.invoice_amount || 0) * 100))
    return total + (d.kind.toLowerCase() === "deduction" ? -cents : cents)
  }, 0)
}

/** Every row without its own check amount takes the header's (the server fills number and date). */
export function with_check_header(parsed: AiParsedResult): AiParsedResult {
  return {
    ...parsed,
    deductions: parsed.deductions.map((d: AiParsedDeduction) => ({
      ...d,
      check_amount: d.check_amount ?? parsed.check_amount ?? null,
    })),
  }
}

/** The proposed backup lines of a deduction; the field is optional on older parses. */
export function backup_lines_of(deduction: AiParsedDeduction): AiParsedBackupLine[] {
  return deduction.backup_lines ?? []
}

/** A new deduction with one backup line's fields updated. Pure so the review edit is testable. */
export function update_backup_line(
  deduction: AiParsedDeduction,
  line_index: number,
  updates: Partial<AiParsedBackupLine>
): AiParsedDeduction {
  return {
    ...deduction,
    backup_lines: backup_lines_of(deduction).map((line: AiParsedBackupLine, i: number) =>
      i === line_index ? { ...line, ...updates } : line
    ),
  }
}

/** A new deduction with one backup line removed. */
export function remove_backup_line(deduction: AiParsedDeduction, line_index: number): AiParsedDeduction {
  return {
    ...deduction,
    backup_lines: backup_lines_of(deduction).filter((_: AiParsedBackupLine, i: number) => i !== line_index),
  }
}

/** Sum of the lines' amounts in cents, so 0.1 + 0.2 style float drift can't creep into the total. */
export function backup_lines_total_cents(lines: AiParsedBackupLine[]): number {
  return lines.reduce((sum: number, line: AiParsedBackupLine) => sum + Math.round((line.amount ?? 0) * 100), 0)
}

/**
 * Whether the lines visibly disagree with the deduction they itemize. Sign is ignored on purpose:
 * plenty of documents itemize a negative deduction in positive line amounts, and flagging every one
 * of those would teach people to ignore the warning.
 */
export function backup_lines_mismatch(deduction: AiParsedDeduction): boolean {
  const lines = backup_lines_of(deduction).filter((line: AiParsedBackupLine) => line.amount != null)
  if (lines.length === 0) return false
  const total = Math.abs(backup_lines_total_cents(lines))
  return Math.abs(total - Math.abs(Math.round(deduction.invoice_amount * 100))) > 1
}

/** Whether a parsed row will be booked as a `kind = Invoice` transaction, which is the only kind that carries a discount. */
export function is_invoice_line(deduction: AiParsedDeduction): boolean {
  return deduction.kind.toLowerCase() === "invoice"
}

function to_cents(amount: number | null | undefined): number | null {
  return amount == null ? null : Math.round(amount * 100)
}

/**
 * The early-pay discount the server will book on an invoice line, in cents: the reviewer's explicit
 * discount when there is one, otherwise the gap between the printed gross and the cash paid. Mirrors
 * `invoice_discount_and_gross` in `server/src/ai_import/confirm.rs`, so what the row shows as
 * "Discount" is what confirm writes to `discount_amount`.
 */
export function invoice_discount_cents(deduction: AiParsedDeduction): number {
  if (!is_invoice_line(deduction)) return 0
  const explicit = to_cents(deduction.discount_amount)
  if (explicit != null && explicit !== 0) return Math.abs(explicit)
  const gross = to_cents(deduction.gross_amount)
  if (gross == null) return 0
  return Math.max(gross - Math.round(deduction.invoice_amount * 100), 0)
}

/**
 * Whether an invoice row's three figures disagree: `gross - discount` must be the amount paid.
 * When it isn't, an adjustment was usually netted into the invoice row and belongs on its own
 * deduction line. Only fires when all three figures are stated — a row with no gross has nothing
 * to disagree with.
 */
export function invoice_amounts_mismatch(deduction: AiParsedDeduction): boolean {
  if (!is_invoice_line(deduction)) return false
  const gross = to_cents(deduction.gross_amount)
  const discount = to_cents(deduction.discount_amount)
  if (gross == null || discount == null) return false
  return Math.abs(gross - Math.abs(discount) - Math.round(deduction.invoice_amount * 100)) > 1
}

function realignPendingToMethod(parsed: AiParsedResult, deductionMethod: string): AiParsedResult {
  const pending = deductionMethod === "Pay Customer Directly"
  return {
    ...parsed,
    deductions: parsed.deductions.map((d: AiParsedDeduction) => ({ ...d, pending })),
  }
}

/** One column of the review table. The set and order differ for a hand-entered check. */
export type Column =
  | "kind"
  | "invoice_number"
  | "amount"
  | "payer"
  | "check_number"
  | "check_date"
  | "po_number"
  | "invoice_date"
  | "execution_date"
  | "description"
  | "pending"

/** The columns a parsed document is reviewed in: everything the model may have read. */
export const REVIEW_COLUMNS: Column[] = [
  "invoice_number",
  "amount",
  "payer",
  "kind",
  "check_number",
  "check_date",
  "po_number",
  "invoice_date",
  "execution_date",
  "description",
  "pending",
]

/** What a hand-entered line needs up front: what it is, which invoice, how much, who paid, a note. */
export const MANUAL_COLUMNS: Column[] = ["kind", "invoice_number", "amount", "payer", "description", "pending"]

/** The fields a typed check rarely needs — the header supplies the check # and date — kept behind "More fields". */
export const DETAIL_COLUMNS: Column[] = ["po_number", "invoice_date", "execution_date", "check_number", "check_date"]

/** The columns the table shows. Details slot in before the description so the free-text column stays last and wide. */
export function visible_columns(manual: boolean, show_details: boolean): Column[] {
  if (!manual) return REVIEW_COLUMNS
  if (!show_details) return MANUAL_COLUMNS
  return ["kind", "invoice_number", "amount", "payer", ...DETAIL_COLUMNS, "description", "pending"]
}

/** The payer a row shows in one field: a known source by name, otherwise whatever was typed for "Other". */
export function payer_of(deduction: AiParsedDeduction): string {
  return deduction.source.toLowerCase() === "other" ? deduction.original_source ?? "" : deduction.source
}

/**
 * A typed payer name, stored so the server resolves it: `source` stays "Other" and the name rides
 * in `original_source`, which confirm matches against the known sources ("KeHE" → Kehe) and keeps
 * verbatim as the customer name when it matches none ("Dynamo").
 */
export function with_payer(text: string): Pick<AiParsedDeduction, "source" | "original_source"> {
  const name = text.trim()
  return { source: "Other", original_source: name === "" ? null : name }
}

export type TieOut = { status: "no_check" } | { status: "ties" } | { status: "off"; diff_cents: number }

/** How the lines stand against the check amount: `diff_cents` is lines minus check, so negative means short. */
export function check_tie_out(check_cents: number | null, total_cents: number): TieOut {
  if (check_cents == null) return { status: "no_check" }
  if (check_cents === total_cents) return { status: "ties" }
  return { status: "off", diff_cents: total_cents - check_cents }
}

const COLUMN_LABELS: Record<Column, string> = {
  kind: "Kind",
  invoice_number: "Invoice #",
  amount: "Amount",
  payer: "Payer",
  check_number: "Check #",
  check_date: "Check date",
  po_number: "PO #",
  invoice_date: "Invoice date",
  execution_date: "Execution date",
  description: "Description",
  pending: "Pending",
}

const COLUMN_TITLES: Partial<Record<Column, string>> = {
  kind: "Invoice and repayment lines add to the check; deductions come off it",
  amount: "As printed; a deduction may be typed with or without its minus sign",
  payer: "Who sent the check: a distributor or retailer we know, or any other customer by name",
  pending: "Still owed: no check has arrived for it yet",
}

/** Column widths, so every row's inputs line up whatever is typed in them. */
const COLUMN_CLASSES: Record<Column, string> = {
  kind: "w-36",
  invoice_number: "w-44",
  amount: "w-40",
  payer: "w-48",
  check_number: "w-28",
  check_date: "w-36",
  po_number: "w-28",
  invoice_date: "w-36",
  execution_date: "w-36",
  description: "w-64",
  pending: "w-20 text-center",
}

export function AiImportReview({ parseResponse, onReset, manual = false, className }: AiImportReviewProps) {
  const methodFromApi = normalizeAiImportMethodForUi(parseResponse.deduction_method)
  const [parsed, setParsed] = useState<AiParsedResult>(
    initializePending(parseResponse.parsed, methodFromApi)
  )
  const [deductionMethod, setDeductionMethod] = useState<string>(methodFromApi)
  const [step, setStep] = useState<Step>("review")
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ConfirmAiImportResponse | null>(null)
  // Which deductions have their backup-lines editor open, keyed like the rows themselves.
  const [expandedLines, setExpandedLines] = useState<Record<string, boolean>>({})
  const [addedLines, setAddedLines] = useState(0)
  // The row just added, so its first field takes focus and typing continues without a click.
  const [focusKey, setFocusKey] = useState<string | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const columns = visible_columns(manual, showDetails)

  function updateDeduction(index: number, updates: Partial<AiParsedDeduction>) {
    setParsed((prev: AiParsedResult) => ({
      ...prev,
      deductions: prev.deductions.map((d: AiParsedDeduction, i: number) =>
        i === index ? { ...d, ...updates } : d
      ),
    }))
  }

  function addDeduction(kind: string) {
    const key = `added-${addedLines + 1}`
    setAddedLines(addedLines + 1)
    setFocusKey(key)
    setParsed((prev: AiParsedResult) => ({
      ...prev,
      deductions: [
        ...prev.deductions,
        blank_line(prev.deductions[prev.deductions.length - 1] ?? null, key, kind, deductionMethod === "Pay Customer Directly"),
      ],
    }))
  }

  function updateCheckHeader(updates: Partial<AiParsedResult>) {
    setParsed((prev: AiParsedResult) => ({ ...prev, ...updates }))
  }

  function removeDeduction(index: number) {
    setParsed((prev: AiParsedResult) => ({
      ...prev,
      deductions: prev.deductions.filter((_: AiParsedDeduction, i: number) => i !== index),
    }))
  }

  function toggleLines(key: string) {
    setExpandedLines((prev: Record<string, boolean>) => ({ ...prev, [key]: !prev[key] }))
  }

  function updateBackupLine(index: number, line_index: number, updates: Partial<AiParsedBackupLine>) {
    setParsed((prev: AiParsedResult) => ({
      ...prev,
      deductions: prev.deductions.map((d: AiParsedDeduction, i: number) =>
        i === index ? update_backup_line(d, line_index, updates) : d
      ),
    }))
  }

  function removeBackupLine(index: number, line_index: number) {
    setParsed((prev: AiParsedResult) => ({
      ...prev,
      deductions: prev.deductions.map((d: AiParsedDeduction, i: number) =>
        i === index ? remove_backup_line(d, line_index) : d
      ),
    }))
  }

  function setDeductionMethodAndRealignPending(method: string) {
    setDeductionMethod(method)
    setParsed((prev: AiParsedResult) => realignPendingToMethod(prev, method))
  }

  async function handleConfirm() {
    setStep("confirming")
    setError(null)
    try {
      const res = await aiImportConfirm({
        s3_uri: parseResponse.s3_uri || null,
        deduction_method: deductionMethod,
        parsed: manual ? with_check_header(parsed) : parsed,
      })
      setResult(res)
      setStep("success")
    } catch (e: any) {
      setError(e.message || "Failed to confirm import")
      setStep("review")
    }
  }

  if (step === "success" && result) {
    return <AiImportSuccess result={result} onReset={onReset} className={className} />
  }

  const line_count = parsed.deductions.length
  const total_cents = lines_total_cents(parsed.deductions)

  return (
    <div class={`space-y-4 ${className || ""}`}>
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex flex-wrap items-center gap-x-5 gap-y-2">
          <div class="flex items-center gap-3">
            <h2 class="text-lg font-semibold">{manual ? "Enter a Check" : "Review Parsed Deductions"}</h2>
            {!manual && (
              <span class={`px-2 py-0.5 rounded text-xs font-medium ${
                parsed.file_type === "check"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-green-100 text-green-800"
              }`}>
                {parsed.file_type === "check" ? "Check" : "Backup"}
              </span>
            )}
          </div>
          <MethodToggle value={deductionMethod} onChange={setDeductionMethodAndRealignPending} />
        </div>
        <Button variant="outline" onClick={onReset}>
          <RotateCcw className="h-4 w-4 mr-1" />
          Start Over
        </Button>
      </div>

      {manual && <ManualCheckHeader parsed={parsed} onChange={updateCheckHeader} />}

      {!manual && parsed.file_type === "check" && (parsed.check_number || parsed.check_amount) && (
        <div class="bg-gray-50 rounded-lg p-3 flex gap-6 text-sm">
          {parsed.check_number && (
            <div>
              <span class="text-gray-500">Check #:</span>{" "}
              <span class="font-medium">{parsed.check_number}</span>
            </div>
          )}
          {parsed.check_date && (
            <div>
              <span class="text-gray-500">Check Date:</span>{" "}
              <span class="font-medium">{parsed.check_date}</span>
            </div>
          )}
          {parsed.check_amount != null && (
            <div>
              <span class="text-gray-500">Check Amount:</span>{" "}
              <span class="font-medium">${parsed.check_amount.toFixed(2)}</span>
            </div>
          )}
        </div>
      )}

      {parseResponse.warning && (
        // Advisory, not blocking: the rows are editable, and fixing a sign here beats a re-parse.
        <div class="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            <strong class="font-medium">These numbers don't add up.</strong> {parseResponse.warning}
          </span>
        </div>
      )}

      {error && (
        <div class="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div class="border border-gray-200 rounded-lg overflow-x-auto">
        {/* Fixed layout: each column keeps its width however much is typed, and extra room is shared
            out; when the detail columns don't fit, the table scrolls rather than squeezing. */}
        <table class="w-full table-fixed text-sm">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              {columns.map((column: Column) => (
                <th
                  key={column}
                  class={`px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500 whitespace-nowrap ${COLUMN_CLASSES[column]}`}
                  title={COLUMN_TITLES[column]}>
                  {column === "payer" && !manual ? "Source / Original" : COLUMN_LABELS[column]}
                </th>
              ))}
              <th class="px-3 py-2 w-10" />
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            {parsed.deductions.map((d: AiParsedDeduction, i: number) => (
              <>
                <DeductionRow
                  key={d.remote_id}
                  deduction={d}
                  columns={columns}
                  manual={manual}
                  autoFocus={d.remote_id === focusKey}
                  linesExpanded={!!expandedLines[`${d.remote_id}:${i}`]}
                  onToggleLines={() => toggleLines(`${d.remote_id}:${i}`)}
                  onChange={(updates: Partial<AiParsedDeduction>) => updateDeduction(i, updates)}
                  onRemove={() => removeDeduction(i)}
                />
                {expandedLines[`${d.remote_id}:${i}`] && backup_lines_of(d).length > 0 && (
                  <BackupLinesEditor
                    key={`${d.remote_id}-lines`}
                    deduction={d}
                    colSpan={columns.length + 1}
                    onChangeLine={(line_index: number, updates: Partial<AiParsedBackupLine>) =>
                      updateBackupLine(i, line_index, updates)
                    }
                    onRemoveLine={(line_index: number) => removeBackupLine(i, line_index)}
                  />
                )}
              </>
            ))}
            {line_count === 0 && (
              <tr>
                <td colSpan={columns.length + 1} class="px-3 py-8 text-center text-gray-500">
                  {manual ? "No lines yet. Add an invoice or a deduction below." : "No deductions found. Try uploading a different document."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => addDeduction("invoice")}>
            <Plus className="h-4 w-4" />
            Invoice
          </Button>
          <Button variant="outline" size="sm" onClick={() => addDeduction("deduction")}>
            <Plus className="h-4 w-4" />
            Deduction
          </Button>
          {manual && (
            <button
              type="button"
              class="ml-2 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
              title="PO #, invoice date, execution date, and a check # or date of the line's own"
              onClick={() => setShowDetails(!showDetails)}>
              {showDetails ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              More fields
            </button>
          )}
        </div>
        <div class="flex items-center gap-4">
          <p class="text-sm text-gray-500 tabular-nums">
            {line_count} line{line_count !== 1 ? "s" : ""}
            {manual && line_count > 0 && (
              <>
                {" · "}
                <span class="font-medium text-gray-900">{formatAmount(total_cents / 100)}</span>
              </>
            )}
          </p>
          <Button
            onClick={handleConfirm}
            disabled={step === "confirming" || line_count === 0}
          >
            {step === "confirming" ? "Importing..." : "Confirm & Import"}
          </Button>
        </div>
      </div>

      {/* The source document, under the rows it produced. Confirming an import is an act of trust in
          a model's reading of a page, and checking it against the page shouldn't mean opening the
          file in another tab and losing the table you are checking. */}
      {parseResponse.s3_uri && (
        <div class="pt-4 border-t border-gray-200">
          <div class="flex items-center justify-between mb-2">
            <h4 class="text-sm font-medium text-gray-700">Source document</h4>
            <a
              href={`/api/_s3?s3_uri=${encodeURIComponent(parseResponse.s3_uri)}`}
              target="_blank"
              rel="noreferrer"
              class="text-xs text-indigo-600 hover:text-indigo-800 hover:underline inline-flex items-center gap-1">
              <ExternalLink className="h-3 w-3" /> Open in a new tab
            </a>
          </div>
          <div class="h-[70vh] border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
            <FileViewer s3Uri={parseResponse.s3_uri} fileName={parseResponse.s3_uri.split("/").pop()} />
          </div>
        </div>
      )}
    </div>
  )
}

interface MethodToggleProps {
  value: string
  onChange: (method: string) => void
}

const METHOD_OPTIONS: { value: string; label: string; title: string }[] = [
  { value: "Pay Customer Directly", label: "Check request", title: "The customer is asking to be paid; the lines stay pending until the check goes out" },
  { value: "DEDUCT", label: "Deducted off check", title: "The customer already took the amounts off the check" },
]

/** How the deductions were applied, as one segmented control rather than two loose radios. */
function MethodToggle({ value, onChange }: MethodToggleProps) {
  return (
    <div class="flex items-center gap-2 text-sm">
      <span class="text-gray-500">Method</span>
      <div class="inline-flex rounded-md border border-gray-200 bg-gray-100 p-0.5" role="radiogroup" aria-label="Deduction method">
        {METHOD_OPTIONS.map(option => {
          const active = value === option.value
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              title={option.title}
              class={`px-3 py-1 rounded transition-colors ${
                active ? "bg-white shadow-sm font-medium text-gray-900" : "text-gray-500 hover:text-gray-900"
              }`}
              onClick={() => onChange(option.value)}>
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface ManualCheckHeaderProps {
  parsed: AiParsedResult
  onChange: (updates: Partial<AiParsedResult>) => void
}

/** The check a hand-entered payment belongs to, with the lines' running total held against it. */
function ManualCheckHeader({ parsed, onChange }: ManualCheckHeaderProps) {
  const total_cents = lines_total_cents(parsed.deductions)
  const check_cents = parsed.check_amount != null ? Math.round(parsed.check_amount * 100) : null
  const tie_out = check_tie_out(check_cents, total_cents)
  return (
    <div class="rounded-lg border border-gray-200 bg-gray-50/60 p-4">
      <div class="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
        <div class="flex flex-wrap gap-4">
          <label class="flex flex-col gap-1.5">
            <span class="text-xs font-medium uppercase tracking-wide text-gray-500">Check #</span>
            <Input
              class="h-9 w-44 bg-white"
              placeholder="e.g. 4543784"
              value={parsed.check_number || ""}
              onChange={(e: Event) => onChange({ check_number: (e.target as HTMLInputElement).value || null })}
            />
          </label>
          <label class="flex flex-col gap-1.5">
            <span class="text-xs font-medium uppercase tracking-wide text-gray-500">Check date</span>
            <Input
              class="h-9 w-40 bg-white"
              type="date"
              value={parsed.check_date || ""}
              onChange={(e: Event) => onChange({ check_date: (e.target as HTMLInputElement).value || null })}
            />
          </label>
          <label class="flex flex-col gap-1.5">
            <span class="text-xs font-medium uppercase tracking-wide text-gray-500">Check amount</span>
            <Input
              class="h-9 w-40 bg-white text-right tabular-nums"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={parsed.check_amount ?? ""}
              onChange={(e: Event) => {
                const value = parseFloat((e.target as HTMLInputElement).value)
                onChange({ check_amount: Number.isNaN(value) ? null : value })
              }}
            />
          </label>
        </div>
        <div class="flex flex-col items-end gap-1.5">
          <span class="text-xs font-medium uppercase tracking-wide text-gray-500">Lines total</span>
          <span class="text-2xl font-semibold leading-9 tabular-nums text-gray-900">{formatAmount(total_cents / 100)}</span>
          <TieOutStatus tie_out={tie_out} />
        </div>
      </div>
      <p class="mt-3 text-xs text-gray-500">
        Invoices and repayments add to the check; deductions come off it. Lines without their own check # or date
        take these.
      </p>
    </div>
  )
}

/** Whether the lines reconcile to the check, in one glance: green when they do, amber with the gap when they don't. */
function TieOutStatus({ tie_out }: { tie_out: TieOut }) {
  if (tie_out.status === "no_check") {
    return <span class="text-xs text-gray-400">Enter the check amount to tie out</span>
  }
  if (tie_out.status === "ties") {
    return (
      <span class="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
        <CheckCircle className="h-3.5 w-3.5" />
        Ties to the check
      </span>
    )
  }
  const gap = formatAmount(Math.abs(tie_out.diff_cents) / 100)
  return (
    <span class="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
      <AlertTriangle className="h-3.5 w-3.5" />
      {tie_out.diff_cents < 0 ? `${gap} short of the check` : `${gap} over the check`}
    </span>
  )
}

interface DeductionRowProps {
  deduction: AiParsedDeduction
  columns: Column[]
  manual: boolean
  autoFocus: boolean
  linesExpanded: boolean
  onToggleLines: () => void
  onChange: (updates: Partial<AiParsedDeduction>) => void
  onRemove: () => void
}

const ROW_INPUT = "h-9 w-full"

function DeductionRow({ deduction, columns, manual, autoFocus, linesExpanded, onToggleLines, onChange, onRemove }: DeductionRowProps) {
  const line_count = backup_lines_of(deduction).length
  const firstInput = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (autoFocus) firstInput.current?.focus()
  }, [autoFocus])

  const text = (field: keyof AiParsedDeduction) => (e: Event) =>
    onChange({ [field]: (e.target as HTMLInputElement).value || null })
  const date = (field: keyof AiParsedDeduction) => (
    <Input class={ROW_INPUT} type="date" value={(deduction[field] as string | null | undefined) || ""} onChange={text(field)} />
  )

  const cells: Record<Column, preact.JSX.Element> = {
    kind: (
      <Select value={deduction.kind} onValueChange={(value: string) => onChange({ kind: value })}>
        <SelectTrigger class={ROW_INPUT}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="invoice">Invoice</SelectItem>
          <SelectItem value="deduction">Deduction</SelectItem>
          <SelectItem value="repayment">Repayment</SelectItem>
          <SelectItem value="prepayment">Prepayment</SelectItem>
        </SelectContent>
      </Select>
    ),
    invoice_number: (
      <>
        <Input
          ref={firstInput}
          class={ROW_INPUT}
          placeholder={manual ? "Invoice #" : undefined}
          value={deduction.invoice_number}
          onChange={(e: Event) => onChange({ invoice_number: (e.target as HTMLInputElement).value })}
        />
        {line_count > 0 && (
          // The AI's proposed itemization of this deduction. It rides under the invoice number
          // rather than in its own column so the table doesn't widen for the many rows without it.
          <button
            type="button"
            class="mt-1 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"
            title="Review the proposed backup lines for this deduction"
            onClick={onToggleLines}>
            {linesExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            <ListTree className="h-3 w-3" />
            {line_count} backup line{line_count !== 1 ? "s" : ""}
          </button>
        )}
      </>
    ),
    amount: (
      <>
        <Input
          class={`${ROW_INPUT} text-right tabular-nums`}
          type="number"
          step="0.01"
          placeholder="0.00"
          title={is_invoice_line(deduction) ? "Cash paid for this invoice (net of any discount)" : undefined}
          value={deduction.invoice_amount}
          onFocus={(e: Event) => (e.target as HTMLInputElement).select()}
          onChange={(e: Event) => onChange({ invoice_amount: parseFloat((e.target as HTMLInputElement).value) || 0 })}
        />
        {is_invoice_line(deduction) && <InvoiceAmountsEditor deduction={deduction} onChange={onChange} />}
      </>
    ),
    payer: manual ? (
      <Input
        class={ROW_INPUT}
        placeholder="KeHE, UNFI, a retailer…"
        value={payer_of(deduction)}
        onChange={(e: Event) => onChange(with_payer((e.target as HTMLInputElement).value))}
      />
    ) : (
      <>
        <Input
          class={ROW_INPUT}
          value={deduction.source}
          onChange={(e: Event) => onChange({ source: (e.target as HTMLInputElement).value })}
        />
        {(deduction.source.toLowerCase() === "other" || deduction.original_source) && (
          <Input
            class={`${ROW_INPUT} mt-1`}
            placeholder="Original source name"
            value={deduction.original_source || ""}
            onChange={(e: Event) => onChange({ original_source: (e.target as HTMLInputElement).value || null })}
          />
        )}
      </>
    ),
    check_number: <Input class={ROW_INPUT} value={deduction.check_number || ""} onChange={text("check_number")} />,
    check_date: date("check_date"),
    po_number: <Input class={ROW_INPUT} value={deduction.po_number || ""} onChange={text("po_number")} />,
    invoice_date: date("invoice_date"),
    execution_date: date("execution_date"),
    description: (
      <Input
        class={ROW_INPUT}
        placeholder={manual ? "What it was for" : undefined}
        value={deduction.description || ""}
        onChange={text("description")}
      />
    ),
    pending: (
      <div class="flex h-9 items-center justify-center">
        <Checkbox
          checked={deduction.pending ?? false}
          // @ts-ignore - Checkbox onCheckedChange type
          onCheckedChange={(checked: boolean) => onChange({ pending: checked })}
        />
      </div>
    ),
  }

  return (
    <tr class="align-top hover:bg-gray-50/60">
      {columns.map((column: Column) => (
        <td key={column} class={`px-3 py-2 ${column === "pending" ? "text-center" : ""}`}>
          {cells[column]}
        </td>
      ))}
      <td class="px-3 py-2">
        <button
          type="button"
          class="flex h-9 items-center text-gray-400 hover:text-red-500"
          onClick={onRemove}
          title="Remove"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  )
}

interface InvoiceAmountsEditorProps {
  deduction: AiParsedDeduction
  onChange: (updates: Partial<AiParsedDeduction>) => void
}

/**
 * The gross and early-pay discount of an invoice line, editable under its cash amount. These are
 * what the accounting page codes on an invoice row, and the model used to have no way to return
 * them, so no AI-imported invoice could ever carry a discount. Confirm books the discount shown
 * here and rebuilds gross as cash + discount. The two fields stay folded into a "Discount" link
 * until the row has one, so a check of plain invoices stays one line per invoice.
 */
function InvoiceAmountsEditor({ deduction, onChange }: InvoiceAmountsEditorProps) {
  const [opened, setOpened] = useState(false)
  const has_figures = deduction.gross_amount != null || deduction.discount_amount != null
  const num = (field: "gross_amount" | "discount_amount") => (e: Event) => {
    const raw = (e.target as HTMLInputElement).value
    onChange({ [field]: raw === "" ? null : parseFloat(raw) || 0 })
  }
  const booked = invoice_discount_cents(deduction) / 100
  if (!opened && !has_figures) {
    return (
      <button
        type="button"
        class="mt-1 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"
        title="Record the gross invoice amount and the early-pay discount taken"
        onClick={() => setOpened(true)}>
        <Plus className="h-3 w-3" />
        Discount
      </button>
    )
  }
  return (
    <div class="mt-1.5 space-y-1 text-xs text-gray-500">
      <div class="space-y-1">
        <label class="flex items-center gap-1.5">
          <span class="w-9 shrink-0">Gross</span>
          <Input
            class="h-7 text-xs text-right tabular-nums"
            type="number"
            step="0.01"
            placeholder="as printed"
            title="Gross / original invoice amount before any early-pay discount"
            value={deduction.gross_amount ?? ""}
            onChange={num("gross_amount")}
          />
        </label>
        <label class="flex items-center gap-1.5">
          <span class="w-9 shrink-0">Disc.</span>
          <Input
            class="h-7 text-xs text-right tabular-nums"
            type="number"
            step="0.01"
            placeholder={booked > 0 ? booked.toFixed(2) : "none"}
            title="Early-pay (terms) discount taken on this invoice, as a positive number"
            value={deduction.discount_amount ?? ""}
            onChange={num("discount_amount")}
          />
        </label>
      </div>
      {invoice_amounts_mismatch(deduction) && (
        <span class="text-amber-700 inline-flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          gross − discount ≠ amount paid
        </span>
      )}
    </div>
  )
}

interface BackupLinesEditorProps {
  deduction: AiParsedDeduction
  colSpan: number
  onChangeLine: (line_index: number, updates: Partial<AiParsedBackupLine>) => void
  onRemoveLine: (line_index: number) => void
}

/**
 * The proposed backup lines of one deduction, editable in place. Rendered as a full-width row
 * under the deduction so the itemization reads as belonging to it, not as more deductions.
 */
function BackupLinesEditor({ deduction, colSpan, onChangeLine, onRemoveLine }: BackupLinesEditorProps) {
  const lines = backup_lines_of(deduction)
  const total = backup_lines_total_cents(lines) / 100
  const text = (line_index: number, field: keyof AiParsedBackupLine) => (e: Event) =>
    onChangeLine(line_index, { [field]: (e.target as HTMLInputElement).value || null })
  const num = (line_index: number, field: keyof AiParsedBackupLine) => (e: Event) => {
    const raw = (e.target as HTMLInputElement).value
    onChangeLine(line_index, { [field]: raw === "" ? null : parseFloat(raw) || 0 })
  }
  return (
    <tr class="bg-indigo-50/40">
      <td colSpan={colSpan} class="px-3 py-2">
        <div class="border border-indigo-100 rounded-md overflow-x-auto bg-white">
          <table class="w-full text-xs">
            <thead class="bg-gray-50 text-gray-500">
              <tr>
                <th class="px-2 py-1.5 text-left font-medium">Description</th>
                <th class="px-2 py-1.5 text-left font-medium">Amount</th>
                <th class="px-2 py-1.5 text-left font-medium">Qty</th>
                <th class="px-2 py-1.5 text-left font-medium">Unit</th>
                <th class="px-2 py-1.5 text-left font-medium">UPC</th>
                <th class="px-2 py-1.5 text-left font-medium">Product #</th>
                <th class="px-2 py-1.5 text-left font-medium">Promo Type</th>
                <th class="px-2 py-1.5 text-left font-medium">Retailer</th>
                <th class="px-2 py-1.5 text-left font-medium">Store</th>
                <th class="px-2 py-1.5 text-left font-medium">PO</th>
                <th class="px-2 py-1.5 w-8" />
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              {lines.map((line: AiParsedBackupLine, line_index: number) => (
                <tr key={line_index} class="hover:bg-gray-50">
                  <td class="px-2 py-1">
                    <Input class="h-7 text-xs min-w-40" value={line.description ?? ""} onChange={text(line_index, "description")} />
                  </td>
                  <td class="px-2 py-1">
                    <Input class="h-7 text-xs w-24" type="number" step="0.01" value={line.amount ?? ""} onChange={num(line_index, "amount")} />
                  </td>
                  <td class="px-2 py-1">
                    <Input class="h-7 text-xs w-16" type="number" step="1" value={line.quantity ?? ""} onChange={num(line_index, "quantity")} />
                  </td>
                  <td class="px-2 py-1">
                    <Input class="h-7 text-xs w-20" type="number" step="0.01" value={line.unit_amount ?? ""} onChange={num(line_index, "unit_amount")} />
                  </td>
                  <td class="px-2 py-1">
                    <Input class="h-7 text-xs w-28" value={line.upc ?? ""} onChange={text(line_index, "upc")} />
                  </td>
                  <td class="px-2 py-1">
                    <Input class="h-7 text-xs w-20" value={line.product_number ?? ""} onChange={text(line_index, "product_number")} />
                  </td>
                  <td class="px-2 py-1">
                    <Input class="h-7 text-xs w-20" value={line.promo_type ?? ""} onChange={text(line_index, "promo_type")} />
                  </td>
                  <td class="px-2 py-1">
                    <Input class="h-7 text-xs w-24" value={line.retailer_name ?? ""} onChange={text(line_index, "retailer_name")} />
                  </td>
                  <td class="px-2 py-1">
                    <Input class="h-7 text-xs w-16" value={line.store_number ?? ""} onChange={text(line_index, "store_number")} />
                  </td>
                  <td class="px-2 py-1">
                    <Input class="h-7 text-xs w-20" value={line.po_number ?? ""} onChange={text(line_index, "po_number")} />
                  </td>
                  <td class="px-2 py-1 text-center">
                    <button
                      class="p-1 text-gray-400 hover:text-red-500 rounded"
                      onClick={() => onRemoveLine(line_index)}
                      title="Remove this backup line">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div class="px-2 py-1.5 border-t border-gray-100 text-xs text-gray-500 flex items-center gap-2">
            <span>
              Lines sum to <span class="font-medium text-gray-700">{formatAmount(total)}</span>
            </span>
            {backup_lines_mismatch(deduction) && (
              <span class="text-amber-700 inline-flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                doesn't match the deduction's {formatAmount(deduction.invoice_amount)}
              </span>
            )}
          </div>
        </div>
      </td>
    </tr>
  )
}

interface AiImportSuccessProps {
  result: ConfirmAiImportResponse
  onReset: () => void
  className?: string
}

function formatAmount(amount: number): string {
  const abs = Math.abs(amount)
  const formatted = abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return amount < 0 ? `-$${formatted}` : `$${formatted}`
}

function formatSource(d: CreatedDeductionSummary): string {
  if (d.source.toLowerCase() === "other" && d.original_source) {
    return d.original_source
  }
  return d.source
}

function AiImportSuccess({ result, onReset, className }: AiImportSuccessProps) {
  return (
    <div class={`space-y-4 ${className || ""}`}>
      <div class="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
        <CheckCircle className="h-6 w-6 text-green-600 shrink-0" />
        <div>
          <h2 class="text-sm font-semibold text-green-800">
            Successfully imported {result.count} deduction{result.count !== 1 ? "s" : ""}
          </h2>
          <p class="text-xs text-green-700 mt-0.5">
            View details or match to a promotion using the links below.
          </p>
        </div>
      </div>

      {result.skipped.length > 0 && (
        <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div class="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
            <h3 class="text-sm font-semibold text-amber-800">
              {result.skipped.length} deduction{result.skipped.length !== 1 ? "s" : ""} skipped
            </h3>
          </div>
          <div class="space-y-1">
            {result.skipped.map((s: SkippedDeductionSummary) => (
              <div key={s.remote_id} class="text-xs text-amber-700 flex items-center gap-2">
                <span class="font-medium">{s.invoice_number || "(no invoice #)"}</span>
                <span class="text-amber-600">— {s.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div class="border rounded-lg divide-y">
        {result.created.map((d: CreatedDeductionSummary) => (
          <div key={d.id} class="px-4 py-3 hover:bg-gray-50 transition-colors">
            <div class="flex items-center justify-between mb-1.5">
              <div class="flex items-center gap-3">
                <span class="text-sm font-semibold text-gray-900">{d.invoice_number}</span>
                <span class="text-sm font-medium text-gray-700">{formatAmount(d.invoice_amount)}</span>
                <span class="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  {formatSource(d)}
                </span>
                <span class="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                  {d.kind}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <a
                  href={`/deduction/${d.id}`}
                  class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <FileText className="h-3.5 w-3.5" />
                  View Deduction
                </a>
                <a
                  href={`/accounting/transaction/${d.id}`}
                  class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <Calculator className="h-3.5 w-3.5" />
                  View Accounting
                </a>
              </div>
            </div>
            <div class="flex items-center gap-4 text-xs text-gray-500">
              {d.check_number && <span>Check #{d.check_number}</span>}
              {d.discount_amount != null && d.discount_amount !== 0 && (
                <span>Discount: {formatAmount(d.discount_amount)}</span>
              )}
              {d.execution_date && <span>Exec: {d.execution_date}</span>}
              {d.description && <span class="truncate max-w-xs">{d.description}</span>}
            </div>
          </div>
        ))}
      </div>

      <div class="flex justify-center pt-2">
        <Button variant="outline" onClick={onReset}>
          <RotateCcw className="h-4 w-4 mr-1" />
          Upload Another Document
        </Button>
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("manual check entry", () => {
    it("opens with one invoice line and no document", () => {
      const r = manual_check_response("DEDUCT")
      expect(r.s3_uri).toBe("")
      expect(r.parsed.file_type).toBe("check")
      expect(r.parsed.deductions).toHaveLength(1)
      expect(r.parsed.deductions[0].kind).toBe("invoice")
      expect(r.parsed.deductions[0].pending).toBe(false)
      expect(manual_check_response("Pay Customer Directly").parsed.deductions[0].pending).toBe(true)
    })

    it("a new line takes the payer of the line above", () => {
      const above = { ...blank_line(null, "a", "invoice", false), source: "Other", original_source: "Dynamo" }
      const next = blank_line(above, "b", "deduction", false)
      expect(next.original_source).toBe("Dynamo")
      expect(next.kind).toBe("deduction")
      expect(next.remote_id).toBe("b")
      expect(next.invoice_number).toBe("")
    })

    it("totals the lines the way the server books each kind", () => {
      const line = (kind: string, invoice_amount: number) => ({ ...blank_line(null, kind, kind, false), invoice_amount })
      // Dynamo 9/25: INV33154 $7,442.50 less a $1,049.06 deduction is the $6,393.44 deposited,
      // whether the deduction is typed positive or negative.
      expect(lines_total_cents([line("invoice", 7442.5), line("deduction", 1049.06)])).toBe(639344)
      expect(lines_total_cents([line("invoice", 7442.5), line("deduction", -1049.06)])).toBe(639344)
      expect(lines_total_cents([line("repayment", 0.1), line("repayment", 0.2)])).toBe(30)
    })

    it("rows take the header's check amount unless they carry their own", () => {
      const parsed = manual_check_response("DEDUCT").parsed
      const own = { ...blank_line(null, "own", "invoice", false), check_amount: 5 }
      const filled = with_check_header({ ...parsed, check_amount: 10296, deductions: [...parsed.deductions, own] })
      expect(filled.deductions.map((d: AiParsedDeduction) => d.check_amount)).toEqual([10296, 5])
    })

    it("shows a hand-entered check the few columns it needs, the rest behind a toggle", () => {
      expect(visible_columns(true, false)).toEqual(["kind", "invoice_number", "amount", "payer", "description", "pending"])
      const all = visible_columns(true, true)
      expect(all).toContain("po_number")
      expect(all).toContain("check_date")
      expect(all[all.length - 2]).toBe("description")
      // A parsed document is reviewed with everything the model may have read, whatever the toggle.
      expect(visible_columns(false, false)).toEqual(REVIEW_COLUMNS)
      expect(visible_columns(false, true)).toEqual(REVIEW_COLUMNS)
    })

    it("reads and writes the payer through one field", () => {
      expect(payer_of({ ...blank_line(null, "a", "invoice", false), source: "Kehe" })).toBe("Kehe")
      expect(payer_of({ ...blank_line(null, "a", "invoice", false), source: "Other", original_source: "Dynamo" })).toBe("Dynamo")
      expect(payer_of(blank_line(null, "a", "invoice", false))).toBe("")
      // The name rides in original_source, where confirm matches it against the known sources.
      expect(with_payer(" KeHE ")).toEqual({ source: "Other", original_source: "KeHE" })
      expect(with_payer("")).toEqual({ source: "Other", original_source: null })
    })

    it("holds the lines against the check amount", () => {
      expect(check_tie_out(null, 639344)).toEqual({ status: "no_check" })
      expect(check_tie_out(639344, 639344)).toEqual({ status: "ties" })
      // Lines short of the check come out negative; lines over it, positive.
      expect(check_tie_out(639344, 634344)).toEqual({ status: "off", diff_cents: -5000 })
      expect(check_tie_out(100, 150)).toEqual({ status: "off", diff_cents: 50 })
    })
  })

  function deduction_with_lines(lines: AiParsedBackupLine[], invoice_amount = -100): AiParsedDeduction {
    return {
      remote_id: "r1",
      invoice_number: "INV-1",
      invoice_amount,
      source: "Kehe",
      kind: "deduction",
      backup_lines: lines,
    } as AiParsedDeduction
  }

  describe("backup line editing", () => {
    it("tolerates parses from before the field existed", () => {
      const old = { remote_id: "r", invoice_number: "i", invoice_amount: -1, source: "Kehe", kind: "deduction" } as AiParsedDeduction
      expect(backup_lines_of(old)).toEqual([])
      expect(remove_backup_line(old, 0).backup_lines).toEqual([])
    })

    it("updates only the targeted line and does not mutate the original", () => {
      const original = deduction_with_lines([{ description: "a", amount: -40 }, { description: "b", amount: -60 }])
      const updated = update_backup_line(original, 1, { amount: -55, upc: "0123" })
      expect(updated.backup_lines).toEqual([
        { description: "a", amount: -40 },
        { description: "b", amount: -55, upc: "0123" },
      ])
      expect(original.backup_lines![1].amount).toBe(-60)
    })

    it("removes exactly the targeted line", () => {
      const original = deduction_with_lines([{ description: "a" }, { description: "b" }, { description: "c" }])
      expect(remove_backup_line(original, 1).backup_lines!.map((l: AiParsedBackupLine) => l.description)).toEqual([
        "a",
        "c",
      ])
    })
  })

  describe("backup_lines_total_cents", () => {
    it("sums in cents so float drift cannot appear in the total", () => {
      // 0.1 + 0.2 !== 0.3 in floats; in cents it is exactly 30.
      expect(backup_lines_total_cents([{ amount: 0.1 }, { amount: 0.2 }])).toBe(30)
      expect(backup_lines_total_cents([{ amount: -40.25 }, { amount: -59.75 }, { amount: null }])).toBe(-10000)
      expect(backup_lines_total_cents([])).toBe(0)
    })
  })

  function invoice(invoice_amount: number, gross_amount?: number | null, discount_amount?: number | null): AiParsedDeduction {
    return { remote_id: "i1", invoice_number: "INV32926", invoice_amount, gross_amount, discount_amount, source: "Other", kind: "invoice" } as AiParsedDeduction
  }

  describe("invoice_discount_cents", () => {
    it("books the explicit discount, whatever its sign", () => {
      expect(invoice_discount_cents(invoice(30363.61, 31020.12, 656.51))).toBe(65651)
      expect(invoice_discount_cents(invoice(98, null, -2))).toBe(200)
    })

    it("derives the discount from gross when the document printed no discount column", () => {
      expect(invoice_discount_cents(invoice(15202.15, 15512.4, null))).toBe(31025)
      // 0.1 + 0.2 style drift cannot appear: everything is compared in cents.
      expect(invoice_discount_cents(invoice(0.1, 0.3, null))).toBe(20)
    })

    it("is zero when there is no discount, and never on a deduction", () => {
      // Super Coffee's PDI INV32926: gross 15,512.40, discount "-", net 15,512.40.
      expect(invoice_discount_cents(invoice(15512.4, 15512.4, null))).toBe(0)
      expect(invoice_discount_cents(invoice(15512.4, null, 0))).toBe(0)
      expect(invoice_discount_cents(invoice(100, 90, null))).toBe(0)
      expect(invoice_discount_cents({ ...invoice(-50, 100, 2), kind: "deduction" })).toBe(0)
    })
  })

  describe("invoice_amounts_mismatch", () => {
    it("is quiet when gross less discount is the amount paid, or when a figure is missing", () => {
      expect(invoice_amounts_mismatch(invoice(30363.61, 31020.12, 656.51))).toBe(false)
      expect(invoice_amounts_mismatch(invoice(15512.4, 15512.4, null))).toBe(false)
      expect(invoice_amounts_mismatch(invoice(15512.4, null, 310.25))).toBe(false)
    })

    it("flags an adjustment netted into the invoice row", () => {
      expect(invoice_amounts_mismatch(invoice(88, 100, 2))).toBe(true)
      expect(invoice_amounts_mismatch({ ...invoice(88, 100, 2), kind: "deduction" })).toBe(false)
    })
  })

  describe("backup_lines_mismatch", () => {
    it("is quiet when the lines account for the deduction, whatever the signs", () => {
      // Documents itemize a negative deduction in positive line amounts all the time.
      expect(backup_lines_mismatch(deduction_with_lines([{ amount: 40 }, { amount: 60 }], -100))).toBe(false)
      expect(backup_lines_mismatch(deduction_with_lines([{ amount: -40 }, { amount: -60 }], -100))).toBe(false)
    })

    it("flags lines that sum to something else", () => {
      expect(backup_lines_mismatch(deduction_with_lines([{ amount: -40 }], -100))).toBe(true)
    })

    it("stays quiet when no line states an amount", () => {
      expect(backup_lines_mismatch(deduction_with_lines([{ description: "qty only", quantity: 3 }], -100))).toBe(false)
      expect(backup_lines_mismatch(deduction_with_lines([], -100))).toBe(false)
    })

    it("tolerates a rounding cent but not a missing line", () => {
      expect(backup_lines_mismatch(deduction_with_lines([{ amount: -33.33 }, { amount: -66.66 }], -99.99))).toBe(false)
      expect(backup_lines_mismatch(deduction_with_lines([{ amount: -33.33 }, { amount: -66.66 }], -100.5))).toBe(true)
    })
  })
}
